import { useAuth0 } from '@auth0/auth0-react';
import { Box, Typography } from '@mui/material';
import { GridColumnVisibilityModel, GridFilterModel, GridSortModel } from '@mui/x-data-grid';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { isGridFilterItemActive } from '@/apis/listGridFilters';
import { GetListParameters } from '@/apis/lists';
import { environment, microfrontendURLPrefix } from '@/config';
import { useList } from '@/pages/ListDetailPage/hooks';
import NotFound from '@/pages/NotFound';
import { adjustPageNo, createBaseContactUrl, createRocketUrlForContact } from '@/util';
import { domainCodeAtom } from '@australiangreens/ag-internal-components';
import AssignListPanelContent from '@components/AssignListPanelContent';
import ContactsTable from '@components/ContactsTable';
import Sidebar from '@components/Sidebar';

const DEFAULT_PAGE_SIZE = 10;
//
export default function NewListPage() {
  const { listId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth0();
  const domainCode = useAtomValue(domainCodeAtom);
  const userSub = user?.sub ?? '';
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [pageNo, setPageNo] = useState(0);
  const offset = pageSize * pageNo;

  const [filterOptions, setFilterOptions] = useState<GridFilterModel>({ items: [] });

  const filters = useMemo(
    () => filterOptions.items.filter(isGridFilterItemActive),
    [filterOptions.items]
  );

  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'id',
      sort: 'asc',
    },
  ]);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({});

  const queryParameters: Omit<GetListParameters, 'token' | 'domainCode'> = {
    limit: pageSize,
    listId: Number(listId),
    offset,
    sortColumn: sortModel[0].field,
    sortOrder: sortModel[0].sort ?? undefined,
    assignedToSub: '',
    filters,
  };

  const { data: list, isLoading, isSuccess } = useList(queryParameters);

  const onSortModelChange = (model: GridSortModel) => {
    if (model.length === 0) {
      setSortModel([{ field: 'id', sort: 'asc' }]);
      setPageNo(0);
    } else if (model[0].field !== sortModel[0].field || model[0].sort !== sortModel[0].sort) {
      setSortModel(model);
      setPageNo(0);
    }
  };

  const handleAssign = (destinationTab: string) => {
    if (destinationTab === 'lists') {
      navigate(`${microfrontendURLPrefix}/lists`);
    } else {
      navigate(`${microfrontendURLPrefix}/lists/${parseInt(listId || '')}/${destinationTab}`);
    }
  };

  const isDev = ['test', 'local', 'dev'].includes(environment);
  const baseContactUrl = createBaseContactUrl(domainCode ?? 'ag', isDev);
  const contactIdUrl = useCallback(
    (id?: number) => createRocketUrlForContact(baseContactUrl, id),
    [baseContactUrl]
  );

  const isAssignee =
    list?.assignedSubs.some((x) => x.assignedSubInfo.id === userSub) ?? false;
  const isCoordinator = list?.coordinatorSubs.some((x) => x.id === userSub) ?? false;

  // Only treat as not-found after a successful load proves the user has no access.
  // Failed/empty fetches must not look like a missing page (e.g. brief outages).
  if (isSuccess && list && !isAssignee && !isCoordinator) {
    return <NotFound />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        height: "inherit",
        flexGrow: 1
      }}>
      <Box
        tabIndex={0}
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          overflow: 'auto'
        }}>
        <Box
          sx={{
            maxWidth: '2000px',
            width: '100%',
            padding: '2rem',
            flexGrow: 1,
            alignSelf: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: '2rem',
          }}
        >
          <Typography component="h2" variant="h5">
            {list?.name}
          </Typography>
          <ContactsTable
            isLoading={isLoading}
            rowCount={list?.count ?? 0}
            data={list?.contactRecords ?? []}
            paginationModel={{ page: pageNo, pageSize }}
            onPaginationModelChange={(model) => {
              setPageSize(model.pageSize);
              setPageNo(
                model.pageSize !== pageSize
                  ? adjustPageNo(pageSize, pageNo, model.pageSize)
                  : model.page
              );
            }}
            onSortModelChange={onSortModelChange}
            sortModel={sortModel}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
            onFilterModelChange={(model) => {
              setPageNo(0);
              setFilterOptions(model);
            }}
            filterMode="server"
            paginationMode="server"
            idUrl={contactIdUrl}
            isRedisStripping={true}
            idWithoutEmptyFilters
          />
        </Box>
      </Box>
      <Sidebar isOpen title="Assignments" position="right">
        {list ? (
          <AssignListPanelContent
            handleAssign={handleAssign}
            list={{
              assignees: list.assignedSubs.map((assignee) => ({
                count: assignee.count ?? 0,
                skippedCount: assignee.assignedSubSkippedCount ?? 0,
                uncalledCount: assignee.assignedSubUncalledCount ?? 0,
                meaningfulInteractionCount: assignee.assignedSubMeaningfulCount ?? 0,
                dateAssigned: assignee.assignedAtFirst,
                ...assignee.assignedSubInfo,
              })),
              coordinators: list.coordinatorSubs,
              ...list,
            }}
          />
        ) : (
          <></>
        )}
      </Sidebar>
    </Box>
  );
}
